import React from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface DetailsStepProps {
  caseDescription: string;
  validation: { valid: boolean; error: string };
  onChange: (field: string, value: string) => void;
  onSubmit: () => void;
  isMobile: boolean;
}

const DetailsStep: React.FC<DetailsStepProps> = ({
  caseDescription,
  validation,
  onChange,
  onSubmit,
  isMobile,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const charCount = caseDescription.length;
  const isValid = charCount >= 40 && charCount <= 500;
  const isTooShort = charCount > 0 && charCount < 40;
  const isTooLong = charCount > 500;
  const isEmpty = charCount === 0;

  const getCharCountColor = () => {
    if (isTooShort || isTooLong) return "#ef4444";
    if (isValid) return "#22c55e";
    return "#94a3b8";
  };

  const getProgressPercentage = () => {
    if (charCount <= 40) return (charCount / 40) * 100;
    return 100;
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        align="center"
        sx={{
          color: "#9ca3af",
          fontWeight: 500,
          mb: 2,
          fontSize: "0.875rem",
        }}
      >
        Question 4 out of 5
      </Typography>

      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#1e293b",
          fontSize: { xs: "1.2rem", md: "1.5rem" },
        }}
      >
        4. {t("form.fourthQuestion.question")}
        <span style={{ color: "#ef4444" }}>*</span>
      </Typography>

      <Box
        sx={{
          position: "relative",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <DescriptionIcon sx={{ color: "#64748b", fontSize: 28 }} />
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            {t("form.fourthQuestion.placeholder")}
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={6}
          placeholder={t("form.fourthQuestion.placeholder")}
          value={caseDescription}
          onChange={(e) => {
            onChange("caseDescription", e.target.value);
          }}
          onKeyDown={handleKeyDown}
          error={!validation.valid && !isEmpty}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              fontSize: "1rem",
              lineHeight: 1.6,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
              "&.Mui-focused": {
                backgroundColor: "#ffffff",
                "& fieldset": {
                  borderColor: isValid ? "#22c55e" : "#3b82f6",
                  borderWidth: "2px",
                },
              },
              "&.Mui-error": {
                "& fieldset": {
                  borderColor: "#ef4444",
                  borderWidth: "2px",
                },
              },
            },
          }}
        />

        {/* Progress bar for minimum character count */}
        {charCount < 40 && charCount > 0 && (
          <Box
            sx={{
              mt: 1,
              height: "4px",
              backgroundColor: "#e2e8f0",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${getProgressPercentage()}%`,
                backgroundColor:
                  getProgressPercentage() === 100 ? "#22c55e" : "#3b82f6",
                transition: "all 0.3s ease",
              }}
            />
          </Box>
        )}

        {validation.error && !isEmpty && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              px: 1,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 18, color: "#ef4444" }} />
            <Typography variant="caption" color="error">
              {validation.error}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1.5,
          py: 2,
          backgroundColor: isValid
            ? "#f0fdf4"
            : isTooShort || isTooLong
            ? "#fef2f2"
            : "#f8fafc",
          borderRadius: "8px",
          border: `1px solid ${
            isValid
              ? "#bbf7d0"
              : isTooShort || isTooLong
              ? "#fecaca"
              : "#e2e8f0"
          }`,
          mb: 3,
          transition: "all 0.2s ease",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isValid ? (
            <CheckCircleIcon sx={{ fontSize: 20, color: "#22c55e" }} />
          ) : isTooShort || isTooLong ? (
            <ErrorOutlineIcon sx={{ fontSize: 20, color: "#ef4444" }} />
          ) : null}
          <Typography
            variant="body2"
            sx={{
              color: getCharCountColor(),
              fontWeight: 500,
            }}
          >
            {isTooShort
              ? t("form.fourthQuestion.minChars")
              : isValid
              ? "Perfect length!"
              : t("form.fourthQuestion.minChars")}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: getCharCountColor(),
            fontWeight: 600,
          }}
        >
          {charCount}/{t("form.fourthQuestion.fiveHundred")}
        </Typography>
      </Box>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={!isValid || isEmpty}
          sx={{
            minWidth: "200px",
            py: 1.5,
            px: 4,
            fontWeight: 600,
            fontSize: "1rem",
            borderRadius: "10px",
            backgroundColor: "#3b82f6",
            textTransform: "none",
            boxShadow: isValid
              ? "0 4px 14px 0 rgba(59, 130, 246, 0.39)"
              : "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#2563eb",
              boxShadow: "0 6px 20px rgba(59, 130, 246, 0.5)",
              transform: "translateY(-1px)",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              color: "#94a3b8",
              boxShadow: "none",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default DetailsStep;
